import { Link } from '@tanstack/react-router';
import type { Permission } from '@abastece/shared';
import { nombreSeccion, permitida, seccionesPara } from '../lib/secciones.js';
import { usePermisos } from '../lib/sesion.js';
import { Pagina } from '../ui/Pagina.js';
import { localeActual, useT, type Clave } from '../i18n/index.js';
import { Chip, Ico, type NombreIcono } from '../ui/Iconos.js';

/** The app itself rather than the shop: learning it, setting it up, and what it has recorded. */
const PIE: readonly {
  nombre: Clave;
  destino: '/aprender' | '/ajustes' | '/bitacora' | '/exportar';
  icono: NombreIcono;
  permisos: readonly Permission[];
}[] = [
  { nombre: 'aprender.titulo', destino: '/aprender', icono: 'aprender', permisos: [] },
  { nombre: 'ajustes.titulo', destino: '/ajustes', icono: 'ajustes', permisos: [] },
  { nombre: 'bitacora.titulo', destino: '/bitacora', icono: 'bitacora', permisos: ['ver_bitacora'] },
  { nombre: 'exportar.titulo', destino: '/exportar', icono: 'exportar', permisos: ['ver_reportes'] },
];

/**
 * El menú: todo lo que no está en la pantalla de inicio.
 *
 * It began as thirteen modules in build order, shown to everybody, a cashier included, who tapped
 * Contabilidad and was told it belongs to the owner. Then it was twenty-one rows grouped by when
 * you would reach for them, filtered by permission. Better, but still one row per screen, and
 * a list that long gets read from the top every time.
 *
 * Now a row is a section of the business (`lib/secciones.ts`), named for what is in it: «Compras
 * y proveedores» is those two screens, with tabs to move between them. The owner has seven rows, a
 * cashier two. Selling and the till's big button are the home screen's alone. Its shortcuts
 * (`lib/atajos.ts`) are a quicker way into screens that are also here, and they stay here: this is
 * the whole map, and a shortcut only skips the walk to one place on it.
 *
 * The app's own pages sit apart at the bottom, smaller, since nobody opens them to run the shop.
 * Ajustes has no permission, because signing out and changing language live there.
 */
export function Mas() {
  const t = useT();
  const puede = usePermisos();
  const locale = localeActual();

  return (
    <Pagina titulo={t('menu.titulo')}>
      <ul className="flex flex-col gap-2" data-guia="mas-grupos">
        {seccionesPara(puede).map(({ seccion, pestanas }) => (
          <li key={seccion.id}>
            <Link to={pestanas[0]!.ruta} className="tarjeta-accion flex items-center gap-4 p-3.5">
              <Chip icono={seccion.icono} tinte={seccion.tinte} />
              <span className="min-w-0 flex-1 font-medium">
                {nombreSeccion(
                  pestanas.map((p) => t(p.nombre)),
                  locale,
                )}
              </span>
              <Ico icono="adelante" className="size-5 shrink-0 text-slate-600" />
            </Link>
          </li>
        ))}
      </ul>

      <nav className="mt-8 flex flex-wrap gap-2 border-t border-slate-800 pt-6">
        {PIE.filter((e) => permitida(e, puede)).map((e) => (
          <Link
            key={e.destino}
            to={e.destino}
            className="btn-secundario h-12 min-h-0 flex-1 basis-24 gap-2 px-3 text-sm font-medium"
          >
            <Ico icono={e.icono} className="size-4 shrink-0" />
            {t(e.nombre)}
          </Link>
        ))}
      </nav>
    </Pagina>
  );
}
